import { ProfileUpdateUserProfileMutation, ProfileUserQuery } from '@/__generated__/graphql';
import { EditIcon } from '@/assets/icons/edit';
import { ProfileMenuIcon } from '@/assets/icons/menu-icons/profile';
import Button from '@/components/basicComponents/button';
import Header, { HeaderIconWrap, HeaderTitle, HeaderTitleWrap } from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { formatUserPhoneNumber } from '@/components/settings-page/users/firmTable/tableBody';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useGoBack } from '@/hooks/useGoBack';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { EmailNotificationFrequency, ROLE } from '@trace-8/trace8-schema';
import { useMemo, useState } from 'react';
import { Helmet as Head } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import useRefresh from '../../hooks/use-refresh';
import EditProfilePage from './EditProfileModalPage';
import ChangePassword from './modals/changePassword';
import ChangePhoneNumber from './modals/changePhoneNumber';
import SignOut from './modals/sign-out';
import { USER_QUERY } from './queries';

const ProfilePage = () => {
  useRefresh();

  const goBack = useGoBack();
  const { user, isAuthenticated } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();

  const backToTitle = searchParams.get('backToTitle') || '';

  const [userData, setUserData] = useState<ProfileUserQuery['user'] | null>(null);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [isEditProfileModalPageOpen, setIsEditProfileModalPageOpen] = useState(false);

  const { loading: userLoading } = useQuery(USER_QUERY, {
    skip: !user?.id,
    variables: {
      id: user?.id
    },
    onCompleted: (data) => {
      setUserData(data.user);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const lastLogin = useMemo(() => {
    const lastLogin = isAuthenticated && userData?.lastLogin ? new Date(Number(userData.lastLogin)) : '';
    const lastLoginDate = lastLogin.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    const lastLoginTime = lastLogin
      .toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      })
      .toLowerCase()
      .replace(' ', '');

    return lastLoginDate.toUpperCase() + ' ' + 'at' + ' ' + lastLoginTime;
  }, [userData]);

  const userInfoItems = [
    [
      { key: 'First Name', value: userData?.firstName ?? '--' },
      { key: 'Last Name', value: userData?.lastName ?? '--' },
      { key: 'Title', value: userData?.title ?? '--' }
    ],
    [
      { key: 'Email', value: userData?.email ?? '--' },
      {
        key: 'Mobile',
        value: formatUserPhoneNumber(userData?.mobilePhoneNumber?.countryCode, userData?.mobilePhoneNumber?.number, '--')
      },
      {
        key: 'Other Phone',
        value: formatUserPhoneNumber(userData?.officePhoneNumber?.countryCode, userData?.officePhoneNumber?.number, '--')
      }
    ],
    [
      {
        key: 'Email Notifications',
        value: EmailNotificationFrequency[userData?.emailNotifications as keyof typeof EmailNotificationFrequency] ?? '--'
      },
      { key: 'Timezone', value: userData?.timezone ?? '--' }
    ]
  ];

  const openModalWindow = (type: string) => {
    setModalWindow({ type, isOpen: true });
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
  };

  const handleEditProfileModalPage = ({ open }: { open: boolean }) => {
    setIsEditProfileModalPageOpen(open);
  };

  const getUpdatedUserData = (updatedUserData: ProfileUpdateUserProfileMutation['updateUserProfile']) => {
    setUserData((prev) => ({ ...prev, ...updatedUserData }));
  };

  if (userLoading) return <LoaderOnWholeScreen />;

  if (isEditProfileModalPageOpen) {
    return (
      <EditProfilePage user={userData} getUpdatedUserData={getUpdatedUserData} handleClosePage={() => handleEditProfileModalPage({ open: false })} />
    );
  }

  return (
    <>
      <Head>
        <title>Profile</title>
      </Head>
      {modalWindow.isOpen && modalWindow.type === 'change-phone-number' && (
        <ChangePhoneNumber isOpen={modalWindow.isOpen} onClose={closeModalWindow} />
      )}
      {modalWindow.isOpen && modalWindow.type === 'change-password' && <ChangePassword isOpen={modalWindow.isOpen} onClose={closeModalWindow} />}
      {modalWindow.isOpen && modalWindow.type === 'sign-out' && <SignOut isOpen={modalWindow.isOpen} onClose={closeModalWindow} />}
      <MainWrap>
        {backToTitle ? (
          <>
            <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/profile' })} backToTitle={backToTitle} />} />
            <PageTitle title="Profile" />
          </>
        ) : (
          <Header
            modalControl={
              <HeaderTitleWrap>
                <HeaderIconWrap>
                  <ProfileMenuIcon style={{ width: isMobile ? 33 : 40, height: isMobile ? 33 : 40 }} fill={themeVar('font.strong')} />
                  <HeaderTitle isTablet={isTablet} isMobile={isMobile}>
                    Profile
                  </HeaderTitle>
                </HeaderIconWrap>
              </HeaderTitleWrap>
            }
          />
        )}
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          <ProfileInformationWrapper>
            <ProfileInformationHeader>
              <ProfileInformationTitle>Profile Information</ProfileInformationTitle>
              <LastLoginSection>
                <LastLoginTitle>Last Login:</LastLoginTitle>
                <LastLoginValue>{lastLogin}</LastLoginValue>
              </LastLoginSection>
            </ProfileInformationHeader>
            <ButtonsWrapper>
              <Button
                size={isMobile ? 'md' : 'lg'}
                styleType={user?.impersonated ? 'disabled' : 'outline'}
                onClick={() => openModalWindow('change-phone-number')}
              >
                Change Mobile
              </Button>
              <Button
                size={isMobile ? 'md' : 'lg'}
                styleType="outline"
                icon={<EditIcon fill={themeVar('action.default')} />}
                onClick={() => handleEditProfileModalPage({ open: true })}
              >
                Edit Profile Info
              </Button>
            </ButtonsWrapper>
            <UserInformation isMobile={isMobile}>
              {userInfoItems.map((row, index) => (
                <UserInformationRow key={index} isMobile={isMobile}>
                  {row.map((item) => (
                    <UserInformationItem key={item.key}>
                      <UserInformationKey>{item.key}</UserInformationKey>
                      <UserInformationValue>{item.value}</UserInformationValue>
                    </UserInformationItem>
                  ))}
                </UserInformationRow>
              ))}
            </UserInformation>
            <UserInformation isMobile={isMobile}>
              <UserInformationRow isMobile={isMobile}>
                <UserInformationItem>
                  <UserInformationKey>Roles</UserInformationKey>
                  <UserInformationValue>{userData?.roles?.map((role) => <p key={role}>{ROLE[role]}</p>)}</UserInformationValue>
                </UserInformationItem>
                <UserInformationItem>
                  <UserInformationKey>Reports To</UserInformationKey>
                  <UserInformationValue>
                    {userData?.supervisor ? `${userData?.supervisor?.firstName} ${userData?.supervisor?.lastName}` : '--'}
                  </UserInformationValue>
                </UserInformationItem>
              </UserInformationRow>
            </UserInformation>
          </ProfileInformationWrapper>
          <ButtonsWrapper>
            <Button size="lg" styleType={user?.impersonated ? 'disabled' : 'outline'} onClick={() => openModalWindow('change-password')}>
              Change Password
            </Button>
            <Button size="lg" onClick={() => openModalWindow('sign-out')}>
              Sign Out
            </Button>
          </ButtonsWrapper>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

export default ProfilePage;

const ProfileInformationWrapper = styled.div`
  margin-top: 40px;
  padding: 20px 40px;
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.1')};
`;

const ProfileInformationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProfileInformationTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  line-height: 22.4px;
  color: ${themeVar('font.strong')};
`;

const LastLoginSection = styled.div`
  font-size: 16px;
  line-height: 22.4px;
`;

const LastLoginTitle = styled.p`
  font-weight: 300;
  color: ${themeVar('font.weak')};
`;

const LastLoginValue = styled.p`
  font-weight: 400;
  color: ${themeVar('font.base')};
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin: 20px 0;
`;

const UserInformation = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? '20px' : '10px')};
  padding: 30px 0;
  border-top: 1px solid ${themeVar('border.base')};
`;

const UserInformationRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
`;

const UserInformationItem = styled.div`
  font-size: 16px;
  line-height: 22.4px;
  width: 33%;
`;

const UserInformationKey = styled.p`
  font-weight: 300;
  color: ${themeVar('font.weak')};
`;

const UserInformationValue = styled.div`
  padding: 4px 0;
  font-weight: 400;
  color: ${themeVar('font.base')};
`;
